<template>
  <div class="mx-auto">
    <loading :active="processing" />
    <v-btn
      color="info"
      icon
      class="float-right ma-8 btn--plain"
      @click="$emit('close:form')"
    >
      <v-icon>mdi-close</v-icon>
    </v-btn>

    <v-form
      ref="form"
      v-model="valid"
      style="width: 80%"
      class="pt-10 pb-4 mx-auto text-left"
    >
      <div class="custom-title pt-8 pb-4">
        Contact Information
      </div>

      <div class="mb-12">
        <span
          v-if="showContactsList"
        >Confirm your contact information. Your selection will appear on your
          court pleadings.</span>
        <span
          v-else
        >We need a bit more information about you. This will appear on your
          court pleadings as your contact information.</span>
      </div>

      <!-- CONTACT INFO BOXES -->
      <div v-if="showContactsList">
        <v-radio-group
          v-model="contactInfo"
          style="font-size: 1rem"
          class="secondary--text mb-8"
          hide-details
        >
          <div
            v-for="info in contactInfos"
            :key="info.id"
            class="d-flex justify-center selection-box my-2 py-4"
            :class="[
              contactInfo.id === info.id ? 'selection-selected' : 'selection',
            ]"
            style="width: 100%; height: auto"
          >
            <v-container>
              <v-row
                no-gutters
                align="center"
              >
                <v-col
                  cols="2"
                  class="d-flex justify-center"
                >
                  <v-radio
                    :value="info"
                    on-icon="mdi-check-circle"
                    off-icon="mdi-check-circle-outline"
                  />
                </v-col>
                <v-col
                  cols="7"
                  class="secondary--text"
                >
                  <div>{{ info.firstName }} {{ info.lastName }}</div>
                  <div class="font-weight-medium">
                    {{ info.company }}
                  </div>
                  <div>
                    {{ info.streetAddress
                    }}<span v-if="info.unitNo">, {{ info.unitNo }}</span>
                  </div>
                  <div>
                    {{ info.city }}, {{ info.state }} {{ info.zipcode }}
                  </div>
                  <div>{{ info.phone }}</div>
                  <div>{{ info.email }}</div>
                </v-col>
                <v-spacer />
                <v-col
                  cols="1"
                  align-self="end"
                >
                  <v-btn
                    color="primary"
                    class="btn--plain"
                    icon
                    small
                    @click="showContactInfoEditModal(info)"
                  >
                    <v-icon small>
                      $pencil
                    </v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-container>
          </div>
        </v-radio-group>
        <v-btn
          v-if="!showForm"
          fab
          depressed
          color="primary"
          height="45"
          width="45"
          @click="toggleForm"
        >
          <span
            class="white--text font-weight-regular"
            style="font-size: 50px"
          >+</span>
        </v-btn>
      </div>

      <!-- CONTACT FORM -->
      <div
        v-if="showForm"
        class="text-left"
      >
        <text-field
          title="First Name"
          :field.sync="contactInfo.firstName"
        />
        <text-field
          title="Last Name"
          :field.sync="contactInfo.lastName"
        />
        <text-field
          title="Company Name"
          :field.sync="contactInfo.company"
          rules-type="none"
        />
        <text-field
          title="Street Address"
          :field.sync="contactInfo.streetAddress"
        />
        <text-field
          title="Apt/Unit"
          :field.sync="contactInfo.unitNo"
          rules-type="none"
        />
        <text-field
          title="City"
          :field.sync="contactInfo.city"
        />
        <select-field
          title="State"
          :field.sync="contactInfo.state"
          :items="states"
        />
        <text-field
          title="Zip Code"
          :field.sync="contactInfo.zipcode"
        />
        <phone-field
          title="Phone Number"
          :field.sync="contactInfo.phone"
        />
        <text-field
          title="Email Address"
          :field.sync="contactInfo.email"
          rules-type="email-required"
        />
      </div>
    </v-form>

    <v-card-actions
      v-if="showForm && !showContactsList"
      style="background-color: #fafbfc; border-radius: 0px 0px 10px 10px"
      class="justify-end py-4 pr-12 mt-12"
    >
      <v-btn
        rounded
        color="accent_light"
        class="mb-2 mt-4 btn--plain capital--btn"
        text
        @click="goToContactList"
      >
        go back
      </v-btn>
      <v-btn
        rounded
        color="accent_light"
        class="px-8 mb-2 mt-4 white--text capital--btn"
        depressed
        :disabled="(showForm && !valid)"
        @click="saveNewContactInfo"
      >
        continue
      </v-btn>
    </v-card-actions>

    <v-card-actions
      v-if="!showForm && showContactsList"
      style="background-color: #fafbfc; border-radius: 0px 0px 10px 10px"
      class="justify-end py-4 pr-12 mt-12"
    >
      <v-btn
        rounded
        color="accent_light"
        class="mb-2 mt-4 btn--plain capital--btn"
        text
        @click="back"
      >
        go back
      </v-btn>
      <v-btn
        rounded
        color="accent_light"
        class="px-8 mb-2 mt-4 white--text capital--btn"
        depressed
        :disabled="(showForm && !valid) || (!showForm && !contactInfo.id)"
        @click="next"
      >
        continue
      </v-btn>
    </v-card-actions>

    <v-dialog
      v-model="showSuccessDialog"
      persistent
      value="''"
      max-width="500"
    >
      <info-dialog
        title="Success!"
        color="primary"
        type="success"
        text="Your records have been updated. Head on over to your Property List to view and manage your properties."
        btn-text="ALL DONE"
        @submit="$router.push(`/properties`)"
      />
    </v-dialog>
    <v-dialog
      v-model="contactInfoEditDialog"
      persistent
      max-width="700"
    >
      <contact-info-edit-dialog
        mode="edit"
        :contact-info="selectedContactInfo" 
        @close="contactInfoEditDialog = false"
        @updateContactInfo="updateContactInfo"
      />
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { states } from "@/resources/index.js";
import Loading from "vue-loading-overlay";
import ContactInfoEditDialog from "@/components/dialogs/ContactInfoEditDialog";
import axios from 'axios';

export default {
  name: "PropertyAddress",
  components: {
    Loading,
    ContactInfoEditDialog,
  },
  data() {
    return {
      valid: true,
      showForm: true,
      showContactsList: false,
      contactInfo: {
        id: "",
        firstName: "",
        lastName: "",
        company: "",
        streetAddress: "",
        unitNo: "",
        city: "",
        state: "New Jersey",
        zipcode: "",
        phone: "",
        email: "",
      },
      contactInfos: [],
      states,
      processing: false,
      // TODO: Edit contact information dialog.
      contactInfoEditDialog: false,
      showSuccessDialog: false,
      selectedContactInfo: {},
      useNext: false,
    };
  },
  computed: {
    ...mapGetters({
      currentProperty: "property/currentProperty",
      caseSteps: "eviction/caseSteps"
    }),
    client() {
      return this.$store.getters.client;
    },
    contactInfoId() {
      return this.currentProperty.contactInfoId;
    },
  },
  async activated() {
    window.scrollTo(0, 0);
    this.useNext = false;

    if (!this.$store.getters.isAuthenticated) {
      console.log("Unauthenticated, getting contact info from localStorage")
      const localContactInfo = localStorage.getItem("contactInfo")
      if (!localContactInfo) return;

      this.contactInfo = JSON.parse(localContactInfo)
      return;
    }

    if (typeof this.$route.params.id !== "string") {
      await this.$store.dispatch("eviction/loadCase", { caseId: this.$route.params.id, logMessage: 'Contact.vue at line 320' })
    }

    this.getAllContactInfos();

    if (this.contactInfoId) {
      this.contactInfo.id = this.contactInfoId;
    }
  },
  async deactivated() {
    await this.saveData();
  },
  methods: {
    async saveData() {
      if (this.useNext) return;

      const {
        params: { id: paramId },
      } = this.$route;

      const isInVC = this.$route.name.includes("vc")
      // const isNewProperty = !isInVC && paramId === "new"
      const isNewCase = isInVC && paramId === "new"

      // if (propertyId && propertyId !== "new") {
      //   this.$router.push({
      //     name: "vc-tenants",
      //     query: {
      //       caseId: this.$store.getters.currentCaseId,
      //     },
      //   });
      // }
      const propertyData = { contactInfoId: this.contactInfo.id, status: "contact" }
      this.$store.dispatch("property/saveProperty", {
        propertyData,
      });

      if(isInVC){
        if (isNewCase) {
          console.log(isNewCase)
        } else {
          await axios.post(
            `${process.env.VUE_APP_URL}/contactInfo`,
            {
              contactInfo: this.contactInfo,
              userId: this.client.userId,
              propertyId: this.currentProperty.propertyId,
            }
          )

          this.$store.commit("property/update_current_property", {
            contactInfo: this.contactInfo,
          });
        }
      } else {
        this.$emit("save:property", propertyData);
        this.showSuccessDialog = true;
      }
    },
    async next() {
      this.useNext = true;

      if (!this.$store.getters.isAuthenticated) {
        localStorage.setItem("contactInfo", this.contactInfo);
        return
      }

      const {
        params: { id: paramId },
      } = this.$route;

      const isInVC = this.$route.name.includes("vc")
      // const isNewProperty = !isInVC && paramId === "new" 
      const isNewCase = isInVC && paramId === "new" 

      // if (propertyId && propertyId !== "new") {
      //   this.$router.push({
      //     name: "vc-tenants",
      //     query: {
      //       caseId: this.$store.getters.currentCaseId,
      //     },
      //   });
      // }
      const propertyData = { contactInfoId: this.contactInfo.id, status: "contact" }
      this.$store.dispatch("property/saveProperty", {
        propertyData,
        steps: {
          type: "completed",
          address: "completed",
          registration: "completed",
          owner: "completed",
          associationToOwner: "completed",
          contact: "completed",
        }
      });

      if(isInVC){
        if (isNewCase) {
          this.$store.dispatch("eviction/createCase", {
            caseData: {
              propertyId: this.currentProperty.propertyId,
            },
            submitted: false,
            lastStep: "Tenant Information",
            nextPath:"vc-tenants"
          });
        } else {
          await axios.post(
            `${process.env.VUE_APP_URL}/contactInfo`,
            {
              contactInfo: this.contactInfo,
              userId: this.client.userId,
              propertyId: this.currentProperty.propertyId,
            }
          )

          this.$store.commit("property/update_current_property", {
            contactInfo: this.contactInfo,
          });
          this.$router.push({ name: "vc-tenants" });
        }
      } else {
        this.$emit("save:property", propertyData);
        this.showSuccessDialog = true;
      }    
    },
    async saveNewContactInfo() {
      if (this.$store.getters.isAuthenticated) {
        await this.saveNewContact()
      } else {
        localStorage.setItem("contactInfo", JSON.stringify(this.contactInfo));
        this.$store.commit("property/update_current_property", {
          contactInfo: this.contactInfo,
        });
        this.$store.dispatch("eviction/createCase", {
          caseData: {
            property: JSON.parse(localStorage.getItem("property")),
            lateCharges: [],
            utilities: [],
            fees: [],
            otherCharges: [],
          },
          submitted: false,
          lastStep: "Tenant Information",
          nextPath: "vc-tenants",
          logMessage: 'Contact.vue at line 385'
        });
        this.$router.push({ name: "vc-tenants" });
      }
    },
    async saveNewContact(){
      this.processing = true;
      if(!this.$refs.form.validate()) return;
      /** Store contact information in database */
      try {
        const result = await axios.post(
          `${process.env.VUE_APP_URL}/contactInfo`,
          {
            contactInfo: this.contactInfo,
            userId: this.client.userId,
          }
        );
        const newContactInfoId = result.data
        this.contactInfo.id = newContactInfoId

        const userData = {
          id: this.client.id,
          auth0Id: this.client.auth0Id,
          defaultContactInfoId: newContactInfoId,
        }
        await this.$store.dispatch("updateUser", userData) 

        this.getAllContactInfos();
        
        this.showForm = false;
        this.showContactsList = true;
        this.processing = false;

        return 
          
      } catch (e) {
        console.log('saveNewContact() error:',e);
        this.processing = false;
      }      
    },
    back() {
      this.$emit("update:steps", {
        type: "completed",
        address: "completed",
        registration: "completed",
        owner: "completed",
        associationToOwner: "completed",
        contact: "",
      });

      this.$router.push({
        name: `${
          this.$route.name.includes("vc") ? "vc-" : ""
        }property-association-to-owner`,
      });
    },
    goToContactList() {
      this.showForm = false
      this.showContactsList = true;
    },
    toggleForm() {
      this.showForm = true;
      this.showContactsList = false;

      this.contactInfo = {
        id: "",
        firstName: "",
        lastName: "",
        company: "",
        streetAddress: "",
        unitNo: "",
        city: "",
        state: "New Jersey",
        zipcode: "",
        phone: "",
        email: "",
      };
    },
    showContactInfoEditModal(info) {
      this.contactInfoEditDialog = true;
      this.selectedContactInfo = info;
    },
    async updateContactInfo() {
      this.contactInfoEditDialog = false;
      try {
        const result = await axios.post(
          `${process.env.VUE_APP_URL}/contactInfo`,
          {
            contactInfo: this.selectedContactInfo,
            userId: this.client.userId,
          }
        );

        if(result.status === 200) {
          this.$http
            .get(`${process.env.VUE_APP_URL}/api/contactInfo/${this.client.id}`)
            .then((r) => {
              this.contactInfos = r.data;
              if (this.contactInfos.length > 0) {
                this.showForm = false;
                this.showContactsList = true;
                this.contactInfo = this.contactInfos.find(info => info.id === this.client.defaultContactInfoId);
              }
            })
            .catch((e) => {
              console.log(e);
            });
        }
        return 
      } catch (e) {
        this.processing = false;
      }
    },
    getAllContactInfos() {
      this.$http
        .get(`${process.env.VUE_APP_URL}/api/contactInfo/${this.client.id}`)
        .then((r) => {
          this.contactInfos = r.data;
          if (this.contactInfos.length > 0) {
            this.showForm = false;
            this.showContactsList = true;
            if (this.currentProperty.contactInfoId) {
              this.contactInfo = this.contactInfos.find(info => info.id === this.currentProperty.contactInfoId);
            } else {
              if (this.client && this.client.defaultContactInfoId) {
                this.contactInfo = this.contactInfos.find(info => info.id === this.client.defaultContactInfoId);
              }
            }
          }
        })
        .catch((e) => {
          console.log(e);
        });
    }
  },
};
</script>

<style></style>
